import actionCreator from "helpers/actionCreator"
import { createReducer } from "reduxsauce"
import { takeLatest, call, select, put, takeEvery } from "redux-saga/effects"
import { put as putApi } from "api"
import { findIndex, get, toLower, isFunction } from "lodash"
import { sendQuery } from "api/graphql"
import update from "immutability-helper"
import { getFavoriteResources } from "./selectors/items"
import { Creators as notificationCreators } from "modules/notifications/model"
import { Creators as modelCreators, Types as modelTypes } from "model/data"
import { Creators as dialogCreators } from "modules/dialog/model"
import { Creators as portfolioRootCreators } from "modules/portfolio-root/model"
import { Types as ResearchTopicCreateTypes } from "modules/research-topic-create/model"
import { getIsOpPro } from "modules/account-root/selectors"
import { getApiErrorMessage } from "./helpers"
import { getCurrentLoadedTopic } from "./selectors/topics"
import { delay } from "redux-saga/effects"
import { getPortfolio } from "modules/edit-portfolio/selectors"

function updatePreferences(state, { favorite, resourceId, resourceType, ticker }) {
  const favoriteResources = getFavoriteResources(state)
  const accountId = get(state, "global.selected.accountId")

  const id = resourceId || ticker

  let newFavoriteResources
  // Removing from favorites
  if (!favorite) {
    const indexToRemove = findIndex(favoriteResources, ({ resourceId: rId }) => id === rId)
    if (indexToRemove > -1) {
      newFavoriteResources = update(favoriteResources, { $splice: [[indexToRemove, 1]] })
    }
  // Add new favorite
  } else {
    newFavoriteResources = update(favoriteResources, {
      $push: [{ resourceId: id, resourceType }],
    })
  }

  return {
    type: "favoriteResources",
    updateCollection: {
      resource: "userFavoriteResources",
      id: `userFavoriteResources-${accountId}`,
    },
    value: [...newFavoriteResources],
  }
}

export const { Types, Creators } = actionCreator("ompnt/modules/resources", {
  toggleFavorite: {
    props: ["resourceId", "resourceType", "favorite"],
    track: true,
    trackType: "resources-favorite",
    trackPayload: ({ favorite, resourceId, resourceType }) => ({ favorite, resourceId, resourceType }),
    updatePreference: {
      getItems: (state, props) => [updatePreferences(state, props)],
    },
  },
  createWatchlist: {
    props: ["id", "data"],
    track: true,
    trackType: "resources-create-complete",
    trackPayload: () => ({ resourceType: "watchlist" }),
  },
  updateWatchlist: {
    props: ["watchlist", "update"],
    track: true,
    trackType: "resources-update-complete",
    trackPayload: () => ({ resourceType: "watchlist" }),
  },
  deleteWatchlist: {
    props: ["watchlist"],
    track: true,
    trackType: "resources-delete-complete",
    trackPayload: () => ({ resourceType: "watchlist" }),
  },
  deletePortfolio: {
    props: ["id", "onDelete"],
    track: true,
    trackType: "resources-delete-complete",
    trackPayload: () => ({ resourceType: "portfolio" }),
  },
  createPortfolio: {
    props: ["data", "upload"],
    track: true,
    trackType: "resources-create-complete",
    trackPayload: () => ({ resourceType: "portfolio" }),
  },
  updatePortfolio: {
    props: ["portfolio", "update", "onSuccess"],
    track: true,
    trackType: "resources-update-complete",
    trackPayload: () => ({ resourceType: "portfolio" }),
  },
  createSwap: {
    props: ["ticker", "data"],
    track: true,
    trackType: "resources-create-complete",
    trackPayload: () => ({ resourceType: "basket" }),
  },
  editSwap: {
    props: ["ticker", "data"],
    track: true,
    trackType: "resources-update-complete",
    trackPayload: () => ({ resourceType: "basket" }),
  },
  deleteSwap: {
    props: ["ticker"],
    track: true,
    trackType: "resources-delete-complete",
    trackPayload: () => ({ resourceType: "basket" }),
  },
  updateTopic: {
    props: ["topicId", "update"],
    track: true,
    trackType: "resources-update-complete",
    trackPayload: () => ({ resourceType: "topic" }),
  },
  deleteTopic: {
    props: ["id"],
    track: true,
    trackType: "resources-delete-complete",
    trackPayload: () => ({ resourceType: "topic" }),
  },
  setFilterBy: {
    props: ["filterBy", "favoritesScreen"],
    trackType: "resources-filter",
    track: true,
    trackPayload: ({ filterBy }) => ({ filterBy }),
  },
  setSortBy: {
    props: ["sortBy", "favoritesScreen"],
    trackType: "resources-sort",
    track: true,
    trackPayload: ({ sortBy }) => ({ sortBy }),
  },
  trackResourceEvent: {
    props: ["eventData"],
    track: true,
    trackType: ({ eventData: { trackType } }) => trackType,
    trackPayload: ({ eventData: { trackPayload } }) => trackPayload,
  },
  addUploadingItem: ["resourceId"],
  setModel: ["modelId"],
  updateRecentResources: ["update", "delete"],
  updateRecentResourceFavorite: ["resourceId", "isFavorite"],
  deleteRecentResource: ["resourceId", "isSwap"],
})

const DEFAULT_STATE = {
  filterBy: null,
  favoritesFilterBy: null,
  sortBy: "lastUpdated",
  favoritesSortBy: "lastUpdated",
  uploadingItems: [],
  selectedModel: null,
  recentResources: [],
}

function handleSetFilterBy(state, { filterBy, favoritesScreen }) {
  const newState = {}

  if (favoritesScreen) {
    newState.favoritesFilterBy = filterBy
  } else {
    newState.filterBy = filterBy
  }

  return { ...state, ...newState }
}

function handleSetSortBy(state, { sortBy, favoritesScreen }) {
  const newState = {}

  if (favoritesScreen) {
    newState.favoritesSortBy = sortBy
  } else {
    newState.sortBy = sortBy
  }

  return { ...state, ...newState }
}

function handleAddUploadingItem(state, { resourceId }) {
  return { ...state, uploadingItems: [...state.uploadingItems, resourceId] }
}

function handleSetModel(state, { modelId }) {
  return { ...state, selectedModel: modelId }
}

function handleUpdateRecentResources(state, { update }) {
  // If we are receiving an array, its coming from the graphql load so we can just override the recentResources prop
  if (Array.isArray(update)) {
    return { ...state, recentResources: update }
  }

  // If we are receiving a single resource, it has been updated and we need to update the recentResources prop accordingly
  const indexToUpdate = findIndex(state.recentResources, ({ id }) => id === update.id)
  if (indexToUpdate > -1) {
    const resources = state.recentResources
    const resource = {
      ...state.recentResources[indexToUpdate],
      ...update,
    }
    resources.splice(indexToUpdate, 1)
    return { ...state, recentResources: [resource, ...resources] }
  }

  return { ...state, recentResources: [update, ...state.recentResources] }
}

function handleUpdateRecentResourceFavorite(state, { resourceId, isFavorite }) {
  const index = findIndex(state.recentResources, ({ id, alias }) => id === resourceId || alias === toLower(resourceId))
  if (index === -1) { return state }

  return update(state, {
    recentResources: { [index]: { $merge: { isFavorite } } },
  })
}

function handleDeleteRecentResource(state, { resourceId, isSwap }) {
  const indexToDelete = findIndex(state.recentResources, (resource) => {
    const { id, alias } = resource
    return !isSwap ? id === resourceId : alias === toLower(resourceId)
  })

  if (indexToDelete === -1) { return state }

  return update(state, {
    recentResources: { $splice: [[indexToDelete, 1]] },
  })
}

export default createReducer(DEFAULT_STATE, {
  [Types.SET_FILTER_BY]: handleSetFilterBy,
  [Types.SET_MODEL]: handleSetModel,
  [Types.SET_SORT_BY]: handleSetSortBy,
  [Types.ADD_UPLOADING_ITEM]: handleAddUploadingItem,
  [Types.UPDATE_RECENT_RESOURCES]: handleUpdateRecentResources,
  [Types.UPDATE_RECENT_RESOURCE_FAVORITE]: handleUpdateRecentResourceFavorite,
  [Types.DELETE_RECENT_RESOURCE]: handleDeleteRecentResource,
})

function* startCreateWatchlist({ data: { name, description } }) {
  const { account } = yield select(state => ({
    account: state.global.selected.account,
  }))

  const mutation = `mutation ($name: String!, $description: String) {
    createWatchlist (name: $name, description: $description) { name, description, id, alias, count, lastUpdated }
  }`

  try {
    const { data, apiMessages } = yield call(sendQuery, mutation, { name, description }, account)
    const result = get(data, "createWatchlist")

    if (result) {
      yield put(notificationCreators.add("info", null, `New watchlist successfully created. You can now access it using the following alias: ${result.alias}`))
      yield put(modelCreators.graphqlCreateSuccess("accountWatchlists", "account-watchlists", "watchlists", result))
      yield put(modelCreators.graphqlCreateSuccess("watchlists", "watchlists", "watchlists", result))
      yield put(modelCreators.resetCollections(["resourcesPermissions"]))

      yield put(dialogCreators.open({
        title: `Would you like to upload securities for ${name} now?`,
        contentText: "This watchlist is now available but does not have any securities. Would you like to upload now?",
        cancelText: "Go Home",
        buttons: [{
          text: "Upload Securities",
          type: "primary",
          trackType: "resources-create-upload",
          trackPayload: { resourceType: "watchlist" },
          action: ({ navigate }) => {
            navigate(`/${account}/watchlist/${result.id}/upload`)
          },
        }],
      }))
    } else {
      yield put(notificationCreators.add("error", null, getApiErrorMessage(apiMessages, "watchlist")))
    }
  } catch {
    yield put(notificationCreators.add("error", null, "An error occurred while creating the watchlist."))
  }
}

function* startUpdateWatchlist({ watchlist, update: { name, description } }) {
  if (watchlist) {
    const { account } = yield select(state => ({
      account: state.global.selected.account,
    }))

    const mutation = `mutation ($id: String!, $watchlist: WatchlistUpdate!) {
      updateWatchlist (id: $id, update: $watchlist) { name, id }
    }`
    const variables = {
      id: watchlist,
      watchlist: { name, description },
    }

    try {
      const { data, apiMessages } = yield call(sendQuery, mutation, variables, account)
      const result = get(data, "updateWatchlist")

      if (result) {
        yield put(dialogCreators.close())
        yield put(modelCreators.resetCollections(["watchlist", "watchlists", "accountWatchlists"]))
        yield put(notificationCreators.add("success", null, "Watchlist successfully updated."))
      } else {
        yield put(notificationCreators.add("error", null, getApiErrorMessage(apiMessages, "watchlist")))
      }
    } catch {
      yield put(notificationCreators.add("error", null, "An unexpected error occurred while updating your watchlist."))
    }
  }
}

function* startDeleteWatchlist({ watchlist }) {
  if (watchlist) {
    const { account } = yield select(state => ({
      account: state.global.selected.account,
    }))

    const mutation = `mutation ($id: String!) {
      deleteWatchlist (id: $id) { ok }
    }`

    try {
      const { data } = yield call(sendQuery, mutation, { id: watchlist }, account)
      const result = get(data, "deleteWatchlist")

      if (result) {
        yield put(modelCreators.resetCollections(["watchlist", "watchlists", "accountWatchlists"]))
        yield put(notificationCreators.add("success", null, "Watchlist successfully deleted."))
      } else {
        yield put(notificationCreators.add("error", null, "An error happened while deleting your watchlist."))
      }
    } catch {
      yield put(notificationCreators.add("error", null, "An unexpected error occurred while deleting your watchlist."))
    }
  }
}

function* startCreateSwap({ ticker, data: { description, terminationDate, currencyOverride } }) {
  const { account } = yield select(state => ({
    account: state.global.selected.account,
  }))

  const mutation = `mutation ($ticker: String!, $description: String!, $terminationDate: Date, $currencyOverride: Currency) {
    createSwap (swap: {ticker: $ticker, description: $description, terminationDate: $terminationDate, currencyOverride: $currencyOverride}) {
      ticker, description, terminationDate, currencyOverride, availableFrom, lastUpdated
    }
  }`

  try {
    const { data, apiMessages } = yield call(sendQuery, mutation, { ticker, description, terminationDate: terminationDate || null, currencyOverride }, account)
    const result = get(data, "createSwap")

    if (result) {
      yield put(notificationCreators.add("info", null, "New basket successfully created."))
      yield put(modelCreators.resetCollections(["baskets", "resourcesPermissions", "resourceMeta"]))
      yield put(dialogCreators.open({
        title: `Would you like to upload positions for ${result.ticker} now?`,
        contentText: "This basket is now available but does not have any position data. Would you like to upload now?",
        cancelText: "Go Home",
        buttons: [{
          text: "Upload Positions",
          type: "primary",
          trackType: "resources-create-upload",
          trackPayload: { resourceType: "basket" },
          action: ({ navigate }) => {
            navigate(`/${account}/baskets/${result.ticker}/upload`)
          },
        }],
      }))
    } else {
      yield put(notificationCreators.add("error", null, getApiErrorMessage(apiMessages, "basket")))
    }
  } catch {
    yield put(notificationCreators.add("error", null, "An error occurred while creating the basket."))
  }
}

function* startEditSwap({ ticker, data: { description, terminationDate, currencyOverride } }) {
  const { account } = yield select(state => ({
    account: state.global.selected.account,
  }))

  const mutation = `mutation ($ticker: String!, $description: String!, $terminationDate: Date, $currencyOverride: Currency) {
    updateSwap (ticker: $ticker  swap: { description: $description, terminationDate: $terminationDate, currencyOverride: $currencyOverride }) {
      ticker, description, terminationDate, currencyOverride, availableFrom
    }
  }`

  try {
    const { data, apiMessages } = yield call(sendQuery, mutation, { ticker, terminationDate: terminationDate || null, description, currencyOverride }, account)
    const result = get(data, "updateSwap")

    if (result) {
      yield put(dialogCreators.close())
      yield put(modelCreators.resetCollections(["swap", "baskets", "resourceMeta"]))
      yield put(notificationCreators.add("success", null, "Basket successfully updated."))
    } else {
      yield put(notificationCreators.add("error", null, getApiErrorMessage(apiMessages, "basket")))
    }
  } catch {
    yield put(notificationCreators.add("error", null, "An unexpected error occurred while updating your basket."))
  }
}

function* startDeleteSwap({ ticker }) {
  if (ticker) {
    const { account } = yield select(state => ({
      account: state.global.selected.account,
    }))

    const mutation = `mutation ($ticker: String!) {
      deleteSwap (ticker: $ticker) { ok }
    }`

    try {
      const { data } = yield call(sendQuery, mutation, { ticker }, account)
      const status = get(data, "deleteSwap.ok")

      if (status) {
        yield put(modelCreators.resetCollections(["swap", "baskets"]))
        yield put(notificationCreators.add("success", null, "Basket successfully deleted."))
      } else {
        yield put(notificationCreators.add("error", null, "An error happened while deleting your basket."))
      }
    } catch {
      yield put(notificationCreators.add("error", null, "An unexpected error occurred while deleting your basket."))
    }
  }
}

function* startCreatePortfolio({ data: { modelId, name, description, live, intraday, betaInstrument, currencyOverride, fxHedgeEnabled } }) {
  try {
    const { account, isOpPro } = yield select(state => ({
      account: state.global.selected.account,
      isOpPro: getIsOpPro(state),
    }))

    const mutation = `mutation ($portfolio: NewPortfolio!) {
      createPortfolio (portfolio: $portfolio) {
        name,
        description,
        id,
        alias,
        defaultModelId,
        rolloverPositionSetToCurrentDate,
        ${isOpPro ? "" : "intradayEnabled,"}
        lastUpdated
      }
    }`
    const variables = {
      portfolio: {
        name,
        description,
        rolloverPositionSetToCurrentDate: !!live,
        defaultModelId: modelId,
        currencyOverride,
        ...(isOpPro ? {} : { intradayEnabled: intraday }),
        ...(!isOpPro && betaInstrument ? { betaInstrument } : {}),
        fxHedgeEnabled,
      },
    }

    const { data, apiMessages } = yield call(sendQuery, mutation, variables, account)
    const result = get(data, "createPortfolio")

    if (result) {
      yield put(notificationCreators.add("info", null, `New portfolio successfully created. You can now access it using the following alias: ${result.alias}`))
      yield put(modelCreators.graphqlCreateSuccess("accountPortfolios", "account-portfolios", "portfolios", result))
      yield put(modelCreators.resetCollections(["resourcesPermissions"]))
      yield put(dialogCreators.open({
        title: `Would you like to upload positions for ${result.name} now?`,
        contentText: "This portfolio is now available but does not have any position data. Would you like to upload now?",
        cancelText: "Go Home",
        buttons: [{
          text: "Upload Positions",
          type: "primary",
          trackType: "resources-create-upload",
          trackPayload: { resourceType: "portfolio" },
          action: ({ navigate }) => {
            navigate(`/${account}/p/${result.id}/upload`)
          },
        }],
      }))
    } else {
      yield put(notificationCreators.add("error", null, getApiErrorMessage(apiMessages, "portfolio")))
    }
  } catch {
    yield put(notificationCreators.add("error", null, "An error occurred while creating the portfolio."))
  }
}

function* startUpdatePortfolio({ portfolio, update: { modelId, name, description, live, intraday, betaInstrument, currencyOverride, fxHedgeEnabled }, onSuccess }) {
  if (portfolio) {
    try {
      const { account, isOpPro } = yield select(state => ({
        account: state.global.selected.account,
        isOpPro: getIsOpPro(state),
      }))

      const editPortfolioData = yield select(state => getPortfolio(state))
      const shouldRefresh = editPortfolioData.fxHedgeEnabled !== fxHedgeEnabled

      const mutation = `mutation ($id: String!, $portfolio: PortfolioUpdate!) {
        updatePortfolio (id: $id, portfolio: $portfolio) {
          name,
          id,
          description,
          alias,
          defaultModelId,
          ${isOpPro ? "" : "intradayEnabled,"}
          rolloverPositionSetToCurrentDate
        }
      }`
      const variables = {
        id: portfolio.id,
        portfolio: {
          name,
          description,
          rolloverPositionSetToCurrentDate: !!live,
          defaultModelId: modelId,
          currencyOverride,
          ...(isOpPro ? {} : { intradayEnabled: intraday }),
          ...(!isOpPro && betaInstrument ? { betaInstrument } : {}),
          fxHedgeEnabled,
        },
      }

      const { data, apiMessages } = yield call(sendQuery, mutation, variables, account)
      const result = get(data, "updatePortfolio")
      const toReset = []

      if (result && shouldRefresh) {
        yield put(dialogCreators.close())
        yield put(notificationCreators.add("success", null, "Portfolio successfully updated. Refreshing page..."))
        yield delay(1500)
        window.location.reload()
        return
      }

      if (result) {
        yield put(dialogCreators.close())
        toReset.push("portfolio", "editPortfolio", "portfolioModelMeta", "accountPortfolios", "portfolioBetaInstrument")
        yield put(notificationCreators.add("success", null, "Portfolio successfully updated."))

        if (isFunction(onSuccess)) {
          onSuccess()
        }
      }

      if (result && modelId !== portfolio.modelId) {
        yield put(portfolioRootCreators.updatePortfolioModelSuccess())
        toReset.push("benchmarksAndPortfolios", "portfolios")
      }

      if (toReset.length > 0) {
        yield put(modelCreators.resetCollections(toReset))
      }

      if (!result) {
        yield put(notificationCreators.add("error", null, getApiErrorMessage(apiMessages, "portfolio")))
      }
    } catch {
      yield put(notificationCreators.add("error", null, "An unexpected error occurred while updating your portfolio."))
    }
  }
}

function* startDeletePortfolio({ id, onDelete }) {
  if (id) {
    try {
      const { account } = yield select(state => ({
        account: state.global.selected.account,
      }))

      const mutation = "mutation ($id: String!) { deletePortfolio (id: $id) { ok } }"

      const { data, apiMessages } = yield call(sendQuery, mutation, { id }, account)
      const status = get(data, "deletePortfolio.ok")

      if (status) {
        yield put(modelCreators.resetCollections(["portfolio", "portfolios", "portfolioModelMeta", "accountPortfolios"]))
        yield put(notificationCreators.add("success", null, "Portfolio successfully deleted."))

        if (isFunction(onDelete)) {
          onDelete()
        }
      } else {
        const errorMessage = get(apiMessages, "errors.0.message", "An error happened while deleting your portfolio.")
        yield put(notificationCreators.add("error", null, errorMessage))
      }
    } catch {
      yield put(notificationCreators.add("error", null, "An unexpected error occurred while deleting your portfolio."))
    }
  }
}

function* startUpdateTopic({ topicId, update }) {
  if (topicId && update) {
    const { account, currentLoadedTopic } = yield select(state => ({
      account: state.global.selected.account,
      currentLoadedTopic: getCurrentLoadedTopic(state),
    }))

    const topic = {
      name: update.name,
      description: update.description,
    }

    const mutation = `mutation ($topicId: String!, $update: UpdateResearchTopic!) {
      updateResearchTopic (researchTopicId: $topicId, researchTopic: $update) {
        id,
        name,
        description,
        referenceInstrument {
          type,
          securityId { ticker, mic, country }
        },
        lastUpdated
      }
    }`

    try {
      const { data, apiMessages } = yield call(sendQuery, mutation, { topicId, update: topic }, account)
      const result = get(data, "updateResearchTopic")

      if (result) {
        yield put(dialogCreators.close())

        const collectionsToReset = ["accountResearchTopics"]
        if (currentLoadedTopic && currentLoadedTopic.id === topicId) {
          collectionsToReset.push("topic")
        }
        yield put(modelCreators.resetCollections(collectionsToReset))

        yield put(notificationCreators.add("success", null, "Research topic successfully updated."))
      } else {
        yield put(notificationCreators.add("error", null, getApiErrorMessage(apiMessages, "research topic")))
      }
    } catch {
      yield put(notificationCreators.add("error", null, "An unknown error occurred while updating your research topic."))
    }
  }
}

function* startDeleteTopic({ id }) {
  if (id) {
    try {
      const { account } = yield select(state => ({
        account: state.global.selected.account,
      }))

      const mutation = "mutation ($id: String!) { deleteResearchTopic (researchTopicId: $id) { ok } }"

      const { data } = yield call(sendQuery, mutation, { id }, account)
      const status = get(data, "deleteResearchTopic.ok")

      if (status) {
        yield put(modelCreators.resetCollections(["accountResearchTopics"]))
        yield put(notificationCreators.add("success", null, "Research topic successfully deleted."))
      } else {
        yield put(notificationCreators.add("error", null, "An error happened while deleting your research topic."))
      }
    } catch {
      yield put(notificationCreators.add("error", null, "An unexpected error occurred while deleting your research topic."))
    }
  }
}

function getResourceIdFromAction(action) {
  const { id, portfolio, watchlist, topicId, ticker } = action
  if (id || topicId || ticker) { return id || topicId || ticker }

  if (portfolio) {
    return get(portfolio, "id")
  }

  if (watchlist) {
    return watchlist
  }
}

function* handleLoadSuccess(action) {
  if (action.resource === "recentResources") {
    yield put(Creators.updateRecentResources(action.data))
  }
}

function* updateRecentResources(action) {
  try {
    const { account } = yield select(state => ({
      account: state.global.selected.account,
    }))

    const resource = yield call(putApi, `/api/${account}/recent-resources`, { resourceId: getResourceIdFromAction(action) })
    yield put(Creators.updateRecentResources(resource))
  } catch {
    // Do nothing, this is a fire and forget call
  }
}

function* deleteRecentResource(action) {
  try {
    const { ticker: isSwap } = action
    yield put(Creators.deleteRecentResource(getResourceIdFromAction(action), isSwap))
  } catch {
    // Do nothing, this is a fire and forget call
  }
}

function* handleToggleFavorite({ resourceId, favorite }) {
  // Update recent resources with the new favorite value
  yield put(Creators.updateRecentResourceFavorite(resourceId, favorite))
}

/**
 * Reset Alpha Theory and Eze metadata, which contains the resources usage/limit when creating/deleting portfolios, swaps, or watchlists
 */
function* resetParterMetadata() {
  yield put(modelCreators.resetCollections(["alphaTheoryMetadata", "ezeMetadata"]))
}

export function* saga() {
  yield takeEvery([
    Types.UPDATE_WATCHLIST,
    Types.EDIT_SWAP,
    Types.UPDATE_PORTFOLIO,
    Types.UPDATE_TOPIC,
  ], updateRecentResources)
  yield takeEvery([
    Types.DELETE_WATCHLIST,
    Types.DELETE_SWAP,
    Types.DELETE_PORTFOLIO,
    Types.DELETE_TOPIC,
  ], deleteRecentResource)

  yield takeEvery([Types.TOGGLE_FAVORITE], handleToggleFavorite)
  yield takeLatest(modelTypes.LOAD_SUCCESS, handleLoadSuccess)

  yield takeLatest(Types.CREATE_WATCHLIST, startCreateWatchlist)
  yield takeLatest(Types.UPDATE_WATCHLIST, startUpdateWatchlist)
  yield takeLatest(Types.DELETE_WATCHLIST, startDeleteWatchlist)
  yield takeLatest(Types.CREATE_SWAP, startCreateSwap)
  yield takeLatest(Types.EDIT_SWAP, startEditSwap)
  yield takeLatest(Types.DELETE_SWAP, startDeleteSwap)
  yield takeLatest(Types.CREATE_PORTFOLIO, startCreatePortfolio)
  yield takeLatest(Types.UPDATE_PORTFOLIO, startUpdatePortfolio)
  yield takeLatest(Types.DELETE_PORTFOLIO, startDeletePortfolio)
  yield takeLatest(Types.UPDATE_TOPIC, startUpdateTopic)
  yield takeLatest(Types.DELETE_TOPIC, startDeleteTopic)

  yield takeLatest([
    Types.DELETE_PORTFOLIO,
    Types.CREATE_PORTFOLIO,
    Types.DELETE_SWAP,
    Types.CREATE_SWAP,
    Types.DELETE_WATCHLIST,
    Types.CREATE_WATCHLIST,
    Types.DELETE_TOPIC,
    ResearchTopicCreateTypes.CREATE_TOPIC,
  ], resetParterMetadata)
}
